import {TranslateService} from '@ngx-translate/core';
import {wordDeclensions} from './word-declensions';
import * as moment from 'moment';
import 'moment-duration-format';
import {TimeagoFormatter} from 'ngx-timeago';

export function timestampToDays(timestamp: number, translateService: TranslateService) {
  const days = timestamp ? timestamp / 86400 : null;
  return days ? days + ' ' + wordDeclensions(days, ['day', 'day_', 'days'], translateService) : '';
}

export function timeToTimestamp(value: number): number | null {
  const hour = new Date(value).getHours() * 3600;
  const minutes = new Date(value).getMinutes() * 60;
  return value ? hour + minutes : null;
}

export function timestampToAge(timestamp: number, translateService: TranslateService) {
  const birthDate = new Date(timestamp * 1000);
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    years--;
  }
  return years + ' ' + wordDeclensions(years, ['year', 'years', 'years_'], translateService);
}

export function timestampToDateTime(seconds: number) {
  return seconds ? moment.unix(seconds).format('DD.MM.YYYY, HH:mm') : '';
}

export function timestampToDateTimeWithoutComma(seconds: number) {
  return seconds ? moment.unix(seconds).format('DD.MM.YYYY HH:mm') : '';
}

export function timestampToFullDateTime(seconds: number) {
  return seconds ? moment.unix(seconds).format('DD.MM.YYYY, HH:mm:ss') : '';
}

export function timestampToFullDateTimeWithoutComma(seconds: number) {
  return seconds ? moment.unix(seconds).format('DD.MM.YYYY HH:mm:ss') : '';
}

export function timestampToDate(seconds: number) {
  return seconds ? moment.unix(seconds).format('DD.MM.YYYY') : '';
}

export function dateStringToFormattedDate(dateStr: string) {
  return dateStr ? moment(dateStr).format('DD.MM.YYYY') : '';
}

export function timestampToTime(seconds: number) {
  return seconds ? moment.utc(seconds * 1000).format('HH:mm:ss') : '';
}

export function timestampToTimeWithoutSeconds(seconds: number) {
  return seconds ? moment.utc(seconds * 1000).format('HH:mm') : '';
}

export function timestampToDuration(seconds: number) {
  return seconds !== null && seconds !== undefined ? moment.duration(seconds, 'seconds').format('HH:mm:ss', {trim: false}) : '';
}

export function timestampToTimeAgo(seconds: number, timeAgoFormatter: TimeagoFormatter) {
  return seconds ? timeAgoFormatter.format(seconds * 1000) : '';
}

export function timestampToTimeApproximately(seconds: number, translateService: TranslateService): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const daySuffix = translateService.instant('d.');
  const hourSuffix = translateService.instant('h.');
  const minuteSuffix = translateService.instant('m.');

  return seconds ? `${days}${daySuffix} ${hours}${hourSuffix} ${minutes}${minuteSuffix}` : '';
}

export function timestampToDurationApproximately(timestamp: number, translateService: TranslateService): string {
  const timeParts = [
    Math.floor(timestamp / 86400),
    Math.floor((timestamp % 86400) / 3600),
    Math.floor((timestamp % 3600) / 60),
    (timestamp % 60)
  ];
  const suffixes = ['d.', 'h.', 'm.', 's.'].map(s => translateService.instant(s));
  const firstTimePartIndex = timeParts.findIndex(timePart => timePart > 0);
  const secondTimePartIndex = timeParts.findIndex(
    (timePart, index) => index > firstTimePartIndex && index <= firstTimePartIndex + 1 && timePart > 0
  );
  const indices = secondTimePartIndex !== -1 ? [firstTimePartIndex, secondTimePartIndex] : [firstTimePartIndex];
  return indices
    .map(index => timeParts[index].toString() + ' ' + suffixes[index])
    .join(' ');
}

export function secondsToTime(value: number): Date | null {
  const time = new Date(1970, 0, 1);
  time.setSeconds(value);
  return value !== null && value !== undefined ? time : undefined;
}
